/**
 * SalesByCountry — regional split of sales, as a summary table plus a stacked
 * monthly trend.
 *
 * The table numbers come straight off `countrySummary`, which the server
 * computes. Re-deriving them here from `countryTrend` would give the dashboard
 * and the scheduled email two implementations of the same figures to keep in
 * step; the chart is the only thing that reshapes the raw points.
 */

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { CountryTrendPoint, CountrySummary } from "../utils/PowerBIApiCall";
import {
  formatCurrency,
  formatCurrencyAxis,
  formatPctSigned,
} from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

interface SalesByCountryProps {
  data: CountryTrendPoint[];
  summary?: CountrySummary;
}

/** Stable colors per region so the chart, legend and table always agree. */
const REGION_COLORS: Record<string, string> = {
  "United States": SERIES.primary,
  Europe: SERIES.primarySoft,
};
const FALLBACK_COLORS = [SERIES.inventory, SERIES.budget, SERIES.neutral];

function colorFor(region: string, idx: number): string {
  return (
    REGION_COLORS[region] ??
    (FALLBACK_COLORS[idx % FALLBACK_COLORS.length] as string)
  );
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, e) => sum + (e.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-xs" style={{ color: entry.color }}>
          <span className="font-medium">{entry.name}:</span>{" "}
          <span className="tabular-nums">{formatCurrency(entry.value)}</span>
        </p>
      ))}
      <p className="text-xs text-gray-500 mt-1 pt-1 border-t border-gray-100">
        <span className="font-medium">Total:</span>{" "}
        <span className="tabular-nums">{formatCurrency(total)}</span>
      </p>
    </div>
  );
}

export function SalesByCountry({ data, summary }: SalesByCountryProps) {
  if (!data || data.length === 0) return null;

  // Regions ordered by total sales so the largest sits at the base of the stack.
  const regions = Array.from(
    data
      .reduce(
        (m, p) => m.set(p.region, (m.get(p.region) ?? 0) + p.totalSales),
        new Map<string, number>(),
      )
      .entries(),
  )
    .sort((a, b) => b[1] - a[1])
    .map(([region]) => region);

  // One region is not a split — nothing to show.
  if (regions.length < 2) return null;

  // Pivot the (region, month) points into one row per month with a column per region.
  const byMonth = new Map<string, Record<string, string | number>>();
  for (const p of data) {
    const row = byMonth.get(p.monthStart) ?? {
      monthStart: p.monthStart,
      periodNameFormatted: p.periodNameFormatted,
    };
    row[p.region] = ((row[p.region] as number) ?? 0) + p.totalSales;
    byMonth.set(p.monthStart, row);
  }
  const chartData = Array.from(byMonth.values()).sort((a, b) =>
    String(a.monthStart).localeCompare(String(b.monthStart)),
  );

  const numCell = "px-4 py-2.5 text-sm text-right tabular-nums";

  return (
    <div className="card overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          Sales by Country
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Country of the booking entity, not the customer&rsquo;s ship-to
          address &middot; past {chartData.length} months
        </p>
      </div>

      {summary && (
        <div className="overflow-x-auto border-b border-gray-100">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-100">
                <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  Region
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  {summary.mtdPeriodName}
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  YTD {summary.year}
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  Prior Year YTD
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  YoY
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  Share of YTD
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r, idx) => (
                <tr key={r.region} className="border-b border-gray-50">
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle"
                      style={{ backgroundColor: colorFor(r.region, idx) }}
                    />
                    {r.region}
                  </td>
                  <td className={`${numCell} text-gray-700`}>
                    {formatCurrency(r.mtdSales)}
                  </td>
                  <td className={`${numCell} font-semibold text-gray-900`}>
                    {formatCurrency(r.ytdSales)}
                  </td>
                  <td className={`${numCell} text-gray-500`}>
                    {r.pyYtdSales === null ? "—" : formatCurrency(r.pyYtdSales)}
                  </td>
                  <td
                    className={`${numCell} font-semibold`}
                    style={{
                      color:
                        r.yoyPct === null
                          ? SERIES.neutral
                          : r.yoyPct >= 0
                            ? SERIES.positive
                            : SERIES.negative,
                    }}
                  >
                    {r.yoyPct === null ? "—" : formatPctSigned(r.yoyPct)}
                  </td>
                  <td className={`${numCell} text-gray-500`}>
                    {r.sharePct.toFixed(1)}%
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50 border-t-2 border-gray-200">
                <td className="px-4 py-2.5 text-sm font-bold text-gray-900">
                  Total
                </td>
                <td className={`${numCell} font-bold text-gray-900`}>
                  {formatCurrency(summary.total.mtdSales)}
                </td>
                <td className={`${numCell} font-bold text-gray-900`}>
                  {formatCurrency(summary.total.ytdSales)}
                </td>
                <td className={`${numCell} font-bold text-gray-600`}>
                  {summary.total.pyYtdSales === null
                    ? "—"
                    : formatCurrency(summary.total.pyYtdSales)}
                </td>
                <td
                  className={`${numCell} font-bold`}
                  style={{
                    color:
                      summary.total.yoyPct === null
                        ? SERIES.neutral
                        : summary.total.yoyPct >= 0
                          ? SERIES.positive
                          : SERIES.negative,
                  }}
                >
                  {summary.total.yoyPct === null
                    ? "—"
                    : formatPctSigned(summary.total.yoyPct)}
                </td>
                <td className={`${numCell} font-bold text-gray-600`}>100.0%</td>
              </tr>
            </tbody>
          </table>
          <p className="px-4 py-2 text-[11px] text-gray-400">
            YoY is omitted where the prior year is absent or negative.
          </p>
        </div>
      )}

      <div className="px-4 py-5">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis
              dataKey="periodNameFormatted"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              angle={-45}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tickFormatter={formatCurrencyAxis}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={65}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="square"
              wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
            />
            {regions.map((region, idx) => (
              <Bar
                key={region}
                dataKey={region}
                name={region}
                stackId="sales"
                fill={colorFor(region, idx)}
                maxBarSize={40}
                // Round only the top segment of the stack.
                radius={idx === regions.length - 1 ? [3, 3, 0, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
